import { useMemo, useState } from 'react'
import NextLink from 'next/link'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'

import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { DialogPelaksanaanAppealRollback } from 'app/KinerjaPage/pages/PelaksanaanV2/components/DialogPelaksanaanAppeal/DialogPelaksanaanAppealRollback'
import { DialogPelaksanaanAppealRequest } from 'app/KinerjaPage/pages/PelaksanaanV2/components/DialogPelaksanaanAppeal/DialogPelaksanaanAppealRequest'
import { DialogPelaksanaanAppealConfirmation } from 'app/KinerjaPage/pages/PelaksanaanV2/components/DialogPelaksanaanAppeal/DialogPelaksanaanAppealConfirmation'
import { PRAKTIK_KINERJA_STEP_ORDER } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import { API_PATH_PELAKSANAAN_PROGRESS } from 'api/kinerja/useAPIKinerja'

import type {
  TFollowUpEvaluationDocumentStates,
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type PersiapanStepCardProps = {
  state?: TStatePelaksanaan
  latestFormState?: TFollowUpEvaluationDocumentStates
  appealState?: TPelaksanaanAppealableStep
}

const CURRENT_STEP: TStatePelaksanaan = 'DOCUMENT_PREPARATION'

export function PersiapanStepCard({
  appealState,
  state,
  latestFormState,
}: PersiapanStepCardProps) {
  const queryClient = useQueryClient()

  // ----------------------------------------------------
  // Step Indices
  // ----------------------------------------------------
  const currentStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(state)
  const persiapanStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(CURRENT_STEP)
  const stepDeficit = currentStepIndex - persiapanStepIndex

  // ----------------------------------------------------
  // Local State
  // ----------------------------------------------------
  const [activeDialog, setActiveDialog] = useState<
    'CONFIRMATION' | 'ROLLBACK' | 'APPEAL' | 'NONE'
  >('NONE')

  // ----------------------------------------------------
  // Derived State
  // ----------------------------------------------------
  const overrideState = useMemo(() => {
    const isPastPreparation = currentStepIndex > persiapanStepIndex
    if (isPastPreparation) return 'done'

    const isCurrentPreparation = currentStepIndex === persiapanStepIndex
    if (isCurrentPreparation) {
      if (latestFormState === 'DRAFT') return 'draft'
      if (latestFormState === 'SUBMITTED') return 'done'
      if (appealState) return 'locked'
      return 'unlocked'
    }

    return 'unlocked'
  }, [currentStepIndex, appealState, latestFormState, persiapanStepIndex])

  const showCTA = overrideState !== 'done'
  const showAppealCTA = overrideState === 'done'
  const isEditDisabled = !!appealState && appealState !== CURRENT_STEP

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------
  const handleClickEdit = () => {
    if (stepDeficit === 1 && latestFormState === 'NOT_CREATED') {
      // Immediately show rollback confirmation dialog
      // If confirmed, the form will immediately be set to DRAFT state
      return setActiveDialog('ROLLBACK')
    }

    // Otherwise, show appeal confirmation dialog
    return setActiveDialog('CONFIRMATION')
  }

  return (
    <>
      <PraktikKinerjaStepCard
        actions={
          showCTA ? (
            <NextLink
              href={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/persiapan`}
              passHref
            >
              <Button
                size="lg"
                as="a"
                className="!inline-flex"
                type={undefined}
                color="blue"
              >
                {overrideState === 'draft' ? 'Lanjutkan' : 'Lakukan'}
              </Button>
            </NextLink>
          ) : showAppealCTA ? (
            <Button
              variant="ghost"
              size="lg"
              color="blue"
              disabled={isEditDisabled}
              className={clsx(
                'gap-1 !bg-white',
                isEditDisabled && '!text-blue-40'
              )}
              onClick={handleClickEdit}
            >
              <Icon color="inherit">edit_outline</Icon>
              Ubah Target Perilaku
            </Button>
          ) : null
        }
        className="max-w-96"
        iconSrc="/illustrations/img-document.png"
        status={
          currentStepIndex === persiapanStepIndex
            ? 'active'
            : currentStepIndex > persiapanStepIndex
            ? 'completed'
            : 'locked'
        }
        title={
          <>
            Pemantauan Kinerja: <br /> Isi Diskusi Persiapan
          </>
        }
        description="Diskusi dengan Tim Kinerja untuk target perilaku, upaya mempelajari, dan jadwal."
      />

      <DialogPelaksanaanAppealRollback
        isOpen={activeDialog === 'ROLLBACK'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        step={CURRENT_STEP as TPelaksanaanAppealableStep}
        url={`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}/pelaksanaan/persiapan`}
        onSuccess={() => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })
        }}
      />
      <DialogPelaksanaanAppealConfirmation
        isOpen={activeDialog === 'CONFIRMATION'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        onConfirm={() => setActiveDialog('APPEAL')}
      />
      <DialogPelaksanaanAppealRequest
        isOpen={activeDialog === 'APPEAL'}
        step={CURRENT_STEP as TPelaksanaanAppealableStep}
        onClose={() => setActiveDialog('NONE')}
        headerText="Jika lanjut ubah, isi seluruh dokumen ini akan dihapus:"
        subHeaderText="Semua dokumen yang Anda dan atasan sudah isi akan dihapus."
        infoText="Anda harus mengajukan perubahan ke atasan. Mohon ketik kalimat ini pada kolom kalimat persetujuan:"
        confirmationText="Saya setuju bahwa isi seluruh dokumen akan dihapus dan saya harus mengulang observasi dari awal."
        initialDialogSize="lg"
        stepDeficit={stepDeficit}
        needWrittenConfirmation
      />
    </>
  )
}
